import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const TOKEN_KEY = 'trustlab_token';
const USER_KEY = 'trustlab_user';

// Endpoints whose 401 response means "the credentials you just typed are
// wrong" (form validation), NOT "your session expired" (real auth failure).
// These must be excluded from the global logout handler — otherwise a user
// who's already signed in (with a JWT) and enters a bad UHID/password on
// the Reports gate would get silently logged out and bounced to /login.
const FORM_AUTH_PATHS = [
  '/auth/verify-credentials',     // UHID gate — step 1 (UHID + password)
  '/auth/verify-credentials-otp', // UHID gate — step 2 (OTP)
  '/auth/change-password',        // wrong "current password" shouldn't log out
  '/auth/verify-otp',             // login OTP — caller already handles 401
];

const isFormAuthRequest = (args) => {
  const url = typeof args === 'string' ? args : args?.url;
  if (!url) return false;
  return FORM_AUTH_PATHS.some((p) => url.startsWith(p));
};

// Wraps fetchBaseQuery with two behaviours:
//   1. Auto-attaches the Bearer token from localStorage.
//   2. On a 401 from a session-protected request (token expired OR account
//      deactivated mid-session), clears the stored session and dispatches a
//      window event that AuthContext + UnauthorizedListener subscribe to.
//      The listener shows a toast and redirects to /login.
//
// Form-auth endpoints (see FORM_AUTH_PATHS above) are excluded — their 401s
// mean "the password/OTP you just typed is wrong" and the caller will show
// an inline error.
export const createAuthBaseQuery = (baseUrl) => {
  const inner = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  });

  return async (args, api, extraOptions) => {
    const result = await inner(args, api, extraOptions);
    if (
      result?.error?.status === 401 &&
      localStorage.getItem(TOKEN_KEY) &&
      !isFormAuthRequest(args)
    ) {
      const message = result.error?.data?.message || 'Your session has ended. Please sign in again.';
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      window.dispatchEvent(new CustomEvent('trustlab:unauthorized', { detail: { message } }));
    }
    return result;
  };
};
